import React, { useState, useRef } from "react";
import { Plus, Upload, Download, Zap, Pencil, Trash2, Mail, Lock, CheckCircle2, Building2 } from "lucide-react";
import * as XLSX from "xlsx";
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useData } from "@/context/DataContext";
import { PageHeader, EventSelector, KpiCard, ProgressBar, NoEvent } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { euro, formatDate, CHART_COLORS } from "@/lib/format";
import client from "@/lib/api";
import { toast } from "sonner";
import {
  PARTICIPANT_CATEGORIES,
  PROVIDER_TYPES,
  STRUCTURE_TYPES,
} from "@/lib/eventConstants";

const CATEGORIES = PARTICIPANT_CATEGORIES;
const PRIVILEGED = PARTICIPANT_CATEGORIES;
const PREFIX = { VIP: "VIP", Presse: "PRESSE", Speaker: "SPEAKER", Jury: "JURY", Coach: "COACH", Sponsor: "SPONSOR", Partenaire: "PARTENAIRE", "Membre du personnel": "PERSONNEL", Exposant: "EXPO", Prestataire: "PRESTA" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { firstName: "", lastName: "", email: "", category: "Prestataire", ticketPrice: 0, specialCode: "", maxCompanions: 0, status: "pending", invitationSent: false };
const CATEGORY_CHART_COLORS = ["#6366f1", "#0ea5e9", "#f59e0b", "#ec4899", "#10b981", "#8b5cf6", "#14b8a6", "#f97316", "#64748b"];

const STATIC = {
  fillRate: 78, confirmedCount: 312, maxParticipants: 400, revenue: 48750, convRate: 84, pendingCount: 23,
  declinedCount: 18, refundedCount: 7, totalInvites: 360,
  byCategory: [
    { name: "Standard", count: 210, confirmed: 189, revenue: 37800 }, { name: "Structure", count: 22, confirmed: 18, revenue: 0 }, { name: "VIP", count: 42, confirmed: 40, revenue: 8000 },
    { name: "Presse", count: 28, confirmed: 28, revenue: 0 }, { name: "Speaker", count: 15, confirmed: 15, revenue: 0 },
    { name: "Jury", count: 11, confirmed: 10, revenue: 0 },
    { name: "Coach", count: 9, confirmed: 8, revenue: 0 },
    { name: "Sponsor", count: 18, confirmed: 18, revenue: 2700 }, { name: "Partenaire", count: 12, confirmed: 10, revenue: 250 },
    { name: "Membre du personnel", count: 35, confirmed: 12, revenue: 0 },
  ],
  codesUsed: { "PRESSE-MARTIN-2026": 3, "PRESSE-DURAND-2026": 5, "VIP-BERNARD-2026": 4, "SPONSOR-ACME-2026": 5 },
  salesByDay: [{ day: "J-60", cumul: 12 }, { day: "J-45", cumul: 48 }, { day: "J-30", cumul: 134 }, { day: "J-21", cumul: 198 }, { day: "J-14", cumul: 267 }, { day: "J-7", cumul: 304 }, { day: "J-3", cumul: 312 }],
};

function genCode(category, lastName) {
  const rand = Array.from({ length: 3 }, () => "ABCDEFGHJKMNPQRSTUVWXYZ23456789"[Math.floor(Math.random() * 31)]).join("");
  return `${PREFIX[category] || "INV"}-${(lastName || "XXX").toUpperCase().slice(0, 6)}-${new Date().getFullYear()}-${rand}`;
}

export default function Invites() {
  const { selectedEvent, guestsByEvent, addGuest, updateGuest, deleteGuest, reloadAll } = useData();
  const [tab, setTab] = useState("invitations");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [selected, setSelected] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [structureOpen, setStructureOpen] = useState(false);
  const [structureForm, setStructureForm] = useState({ structureName: "", structureType: "Entreprise", firstName: "", lastName: "", email: "", maxUses: 10 });
  const fileRef = useRef(null);

  const submitStructure = async () => {
    if (!structureForm.structureName.trim() || !structureForm.email.trim()) { toast.error("Nom de structure et email requis"); return; }
    if (!EMAIL_RE.test(structureForm.email.trim())) { toast.error("Email invalide"); return; }
    const maxU = Math.max(1, Number(structureForm.maxUses) || 1);
    const code = `STRUCT-${(structureForm.structureName || "STR").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8)}-${new Date().getFullYear()}-${Array.from({ length: 4 }, () => "ABCDEFGHJKMNPQRSTUVWXYZ23456789"[Math.floor(Math.random() * 31)]).join("")}`;
    await addGuest({
      kind: "structure",
      structureName: structureForm.structureName.trim(),
      structureType: structureForm.structureType,
      firstName: structureForm.firstName.trim() || structureForm.structureName.trim(),
      lastName: structureForm.lastName.trim(),
      email: structureForm.email.trim(),
      category: "Structure",
      ticketPrice: 0,
      specialCode: code,
      maxUses: maxU,
      codeUses: 0,
      maxCompanions: 0,
      status: "pending",
      invitationSent: false,
      eventId: selectedEvent.id,
    });
    setStructureOpen(false);
    setStructureForm({ structureName: "", structureType: "Entreprise", firstName: "", lastName: "", email: "", maxUses: 10 });
    toast.success(`🏢 Structure ajoutée · code ${code} valable ${maxU} inscription(s)`);
  };

  const onImportExcel = async (ev) => {
    const file = ev.target.files?.[0]; if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf);
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
      let n = 0;
      for (const r of rows) {
        const get = (keys) => { for (const k of Object.keys(r)) { if (keys.some((x) => k.toLowerCase().includes(x))) return r[k]; } return ""; };
        const firstName = String(get(["prénom", "prenom", "first"]) || "");
        const lastName = String(get(["nom", "last"]) || "");
        const email = String(get(["email", "mail", "courriel"]) || "");
        const rawCat = String(get(["catég", "categ", "category"]) || "");
        if (!firstName && !lastName && !email) continue;
        const category = CATEGORIES.includes(rawCat) ? rawCat : "";
        await addGuest({ firstName, lastName, email, category, ticketPrice: 0, maxCompanions: 0, status: "pending", invitationSent: false, eventId: selectedEvent.id });
        n++;
      }
      toast.success(`${n} invité(s) importé(s) depuis le fichier`);
    } catch { toast.error("Fichier illisible — attendu CSV ou XLSX"); } finally { ev.target.value = ""; }
  };

  const applyCategory = async (cat) => {
    for (const id of selected) {
      const g = guests.find((x) => x.id === id); if (!g) continue;
      const patch = { ...g, category: cat };
      if (PRIVILEGED.includes(cat)) { patch.specialCode = g.specialCode || genCode(cat, g.lastName); patch.maxCompanions = g.maxCompanions || 1; }
      await updateGuest(id, patch);
    }
    toast.success(`Catégorie "${cat}" appliquée${PRIVILEGED.includes(cat) ? " · codes générés" : ""}`);
    setSelected([]);
  };

  const [bulkComp, setBulkComp] = useState(0);
  const [pendingCat, setPendingCat] = useState("");
  const generateCodesBulk = async () => {
    for (const id of selected) {
      const g = guests.find((x) => x.id === id); if (!g) continue;
      const cat = g.category || "VIP";
      await updateGuest(id, { ...g, category: g.category || cat, maxCompanions: Number(bulkComp) || 0, specialCode: genCode(cat, g.lastName) });
    }
    toast.success(`Codes générés pour ${selected.length} invité(s) · ${bulkComp} accompagnant(s)`);
    setSelected([]);
  };

  if (!selectedEvent) return (<><PageHeader title="Billetterie" /><NoEvent /></>);

  const guests = guestsByEvent(selectedEvent.id).filter((g) => g.status !== "deleted" && g.source !== "registered");
  const deleted = guestsByEvent(selectedEvent.id).filter((g) => g.status === "deleted");
  const filtered = guests
    .filter((g) => statusFilter === "all"
      || (statusFilter === "pending" ? (!g.invitationSent && g.status !== "declined" && g.status !== "confirmed")
        : statusFilter === "declined" ? g.status === "declined"
        : statusFilter === "confirmed" ? g.status === "confirmed"
        : (g.invitationSent && g.status !== "declined" && g.status !== "confirmed")))
    .filter((g) => catFilter === "all" || (catFilter === "Autre" ? !g.category : g.category === catFilter))
    .filter((g) => !search || `${g.firstName} ${g.lastName} ${g.email}`.toLowerCase().includes(search.toLowerCase()));

  const openNew = () => { setForm(EMPTY); setEditId(null); setShowForm(true); };

  const submit = async () => {
    if (!form.firstName || !form.email) { toast.error("Prénom et email requis"); return; }
    if (!EMAIL_RE.test(form.email.trim())) { toast.error("L'email n'est pas valide"); return; }
    const payload = { ...form, ticketPrice: Number(form.ticketPrice) || 0, maxCompanions: Number(form.maxCompanions) || 0, eventId: selectedEvent.id };
    if (PRIVILEGED.includes(payload.category) && !payload.specialCode) {
      payload.specialCode = genCode(payload.category, payload.lastName);
    }
    if (editId) await updateGuest(editId, payload); else await addGuest(payload);
    toast.success(editId ? "Invité modifié" : "Invité ajouté");
    setShowForm(false); setForm(EMPTY); setEditId(null);
  };

  const removeGuest = async (g) => { if (g.invitationSent) return; await updateGuest(g.id, { ...g, status: "deleted" }); toast.success("Invité supprimé"); };

  const sendInvites = async (ids, force = false) => {
    const toSend = guests.filter((g) => ids.includes(g.id) && (force || !g.invitationSent));
    if (!toSend.length) { toast.info("Aucune invitation à envoyer"); return; }
    // Vérifier qu'une landing page publiée existe pour l'événement — sinon prévenir
    try {
      const { data: pages } = await client.get(`/landing/pages?eventId=${selectedEvent.id}`);
      const hasPublished = (pages || []).some((p) => p.published);
      if (!hasPublished) {
        const ok = window.confirm(
          "⚠️  Aucune landing page publiée n'a été détectée pour cet événement.\n\n" +
          "Les invités vont recevoir un lien qui pourrait ne mener à aucune page d'inscription. " +
          "Souhaitez-vous quand même envoyer les invitations ?\n\n" +
          "OK = envoyer   ·   Annuler = créer d'abord la landing page"
        );
        if (!ok) return;
      }
    } catch { /* endpoint absent — on continue */ }
    try {
      const { data } = await client.post("/guests/invite", { guest_ids: toSend.map((g) => g.id), force });
      await reloadAll(); setSelected([]);
      toast.success(force ? `${data.sent} invitation(s) renvoyée(s)` : `${data.sent} invitation(s) envoyée(s)`);
      if (data.errors?.length) toast.error(data.errors[0]);
    } catch { toast.error("Erreur lors de l'envoi"); }
  };

  const toggleAll = (checked) => setSelected(checked ? filtered.filter((g) => !g.invitationSent).map((g) => g.id) : []);

  const kpis = {
    total: guests.length,
    pending: guests.filter((g) => !g.invitationSent && g.status !== "declined" && g.status !== "confirmed").length,
    invited: guests.filter((g) => g.invitationSent && g.status !== "declined" && g.status !== "confirmed").length,
    confirmed: guests.filter((g) => g.status === "confirmed").length,
    declined: guests.filter((g) => g.status === "declined").length,
    deleted: deleted.length,
  };
  const showPrivileged = PRIVILEGED.includes(form.category);

  return (
    <>
      <PageHeader title="Billetterie" actions={<EventSelector />} />

      <div className="flex items-center justify-between border-b border-border mb-6 flex-wrap gap-3">
        <div className="flex gap-6">
          <button onClick={() => setTab("invitations")} data-testid="tab-invitations" className={`pb-2 text-sm font-semibold border-b-2 ${tab === "invitations" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>📋 Invitations</button>
          <button onClick={() => setTab("participants")} data-testid="tab-participants" className={`pb-2 text-sm font-semibold border-b-2 ${tab === "participants" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>👥 Participants</button>
          <button onClick={() => setTab("results")} data-testid="tab-results" className={`pb-2 text-sm font-semibold border-b-2 ${tab === "results" ? "border-primary text-primary" : "border-transparent text-muted-foreground"}`}>📊 Résultats</button>
        </div>
        {tab === "invitations" && (
          <div className="flex gap-2 pb-2 flex-wrap">
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onImportExcel} data-testid="import-excel-input" />
            <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="import-excel"><Upload className="h-4 w-4 mr-1" />Importer Excel</Button>
            <Button variant="outline" size="sm" onClick={() => toast.info("Export généré")} data-testid="export-excel"><Download className="h-4 w-4 mr-1" />Exporter Excel</Button>
            <Button variant="outline" size="sm" onClick={() => setStructureOpen(true)} data-testid="add-structure-button"><Building2 className="h-4 w-4 mr-1" />Ajouter une structure</Button>
            <Button size="sm" onClick={openNew} data-testid="add-guest-button"><Plus className="h-4 w-4 mr-1" />Ajouter un invité</Button>
          </div>
        )}
      </div>

      {tab === "invitations" ? (
        <>
          <Dialog open={structureOpen} onOpenChange={setStructureOpen}>
            <DialogContent data-testid="structure-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5 text-teal-600" />Inviter une structure</DialogTitle>
                <DialogDescription>Une structure (entreprise, association, université…) reçoit un <b>code multi-usage</b> lui permettant d'inscrire plusieurs clients / membres avec un seul et même code.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="mb-1.5 block">Nom de la structure *</Label><Input value={structureForm.structureName} onChange={(e) => setStructureForm({ ...structureForm, structureName: e.target.value })} placeholder="Ex : Acme Corp, Association X, Université Y" data-testid="structure-name" /></div>
                <div><Label className="mb-1.5 block">Type de structure *</Label><Select value={structureForm.structureType} onValueChange={(value) => setStructureForm({ ...structureForm, structureType: value })}><SelectTrigger data-testid="structure-type"><SelectValue /></SelectTrigger><SelectContent>{STRUCTURE_TYPES.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent></Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="mb-1.5 block">Prénom contact</Label><Input value={structureForm.firstName} onChange={(e) => setStructureForm({ ...structureForm, firstName: e.target.value })} placeholder="Marie" data-testid="structure-first" /></div>
                  <div><Label className="mb-1.5 block">Nom contact</Label><Input value={structureForm.lastName} onChange={(e) => setStructureForm({ ...structureForm, lastName: e.target.value })} placeholder="Dupont" data-testid="structure-last" /></div>
                </div>
                <div><Label className="mb-1.5 block">Email contact *</Label><Input type="email" value={structureForm.email} onChange={(e) => setStructureForm({ ...structureForm, email: e.target.value })} placeholder="contact@structure.fr" data-testid="structure-email" /></div>
                <div><Label className="mb-1.5 block">Nombre d'inscriptions maximum *</Label><Input type="number" min="1" value={structureForm.maxUses} onChange={(e) => setStructureForm({ ...structureForm, maxUses: e.target.value })} data-testid="structure-max-uses" /><p className="text-[11px] text-muted-foreground mt-1">Le code sera valable pour ce nombre de billets. Chaque inscription décrémente le compteur.</p></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStructureOpen(false)}>Annuler</Button>
                <Button onClick={submitStructure} data-testid="structure-submit" className="bg-teal-600 hover:bg-teal-700">Créer la structure</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <KpiCard tint="indigo" label="Total" value={kpis.total} />
            <KpiCard tint="amber" label="En attente" value={kpis.pending} />
            <KpiCard tint="emerald" label="Invités" value={kpis.invited} />
            <KpiCard tint="violet" label="Confirmés" value={kpis.confirmed} />
            <KpiCard tint="rose" label="Déclinés" value={kpis.declined} />
            <KpiCard tint="rose" label="Supprimés" value={kpis.deleted} />
          </div>

          <div className="flex flex-wrap gap-3 mb-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}><SelectTrigger className="w-44" data-testid="guest-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tous</SelectItem><SelectItem value="pending">En attente</SelectItem><SelectItem value="sent">Invitation envoyée</SelectItem><SelectItem value="confirmed">Confirmés</SelectItem><SelectItem value="declined">Invitation déclinée</SelectItem></SelectContent></Select>
            <Select value={catFilter} onValueChange={setCatFilter}><SelectTrigger className="w-44" data-testid="guest-cat-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Toutes catégories</SelectItem><SelectItem value="Structure">Structure</SelectItem><SelectItem value="Autre">Autre (sans catégorie)</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            <Input placeholder="Rechercher..." className="w-56" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="guest-search" />
          </div>

          {selected.length > 0 && (
            <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-lg p-3 mb-4">
              <span className="text-sm font-medium">{selected.length} sélectionné(s)</span>
              <Button size="sm" onClick={() => sendInvites(selected)} data-testid="bulk-invite"><Mail className="h-4 w-4 mr-1" />Envoyer invitation</Button>
              <Select value={pendingCat} onValueChange={setPendingCat}><SelectTrigger className="w-44 h-9" data-testid="bulk-category"><SelectValue placeholder="Choisir catégorie" /></SelectTrigger><SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              <Button size="sm" variant="secondary" disabled={!pendingCat} onClick={() => { applyCategory(pendingCat); setPendingCat(""); }} data-testid="bulk-category-validate">Valider la catégorie</Button>
              <Input type="number" min="0" placeholder="Accomp." className="w-24 h-9" value={bulkComp} onChange={(e) => setBulkComp(e.target.value)} data-testid="bulk-companions" />
              <Button size="sm" variant="outline" onClick={generateCodesBulk} data-testid="bulk-generate-codes"><Zap className="h-4 w-4 mr-1" />Générer codes</Button>
              <Button size="sm" variant="outline" onClick={() => { guests.filter((g) => selected.includes(g.id) && !g.invitationSent).forEach((g) => removeGuest(g)); setSelected([]); }}>Supprimer</Button>
            </div>
          )}

          {showForm && (
            <div className="bg-card rounded-xl border border-border p-6 mb-6">
              <h3 className="font-display font-semibold mb-4">{editId ? "Modifier l'invité" : "Nouvel invité"}</h3>
              <div className="grid sm:grid-cols-3 gap-4">
                <div><Label className="mb-1.5 block">Prénom</Label><Input data-testid="guest-firstname" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Nom</Label><Input data-testid="guest-lastname" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Email</Label><Input type="email" data-testid="guest-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Numéro de téléphone</Label><Input type="tel" data-testid="guest-phone" value={form.phone || ""} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label className="mb-1.5 block">Catégorie</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="guest-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
                <div><Label className="mb-1.5 block">Nombre d'accompagnants</Label><Input type="number" min="0" data-testid="guest-companions-count" value={form.maxCompanions} onChange={(e) => setForm({ ...form, maxCompanions: e.target.value })} /></div>
              </div>
              {form.category === "Prestataire" && <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="guest-prestataire-fields"><div><Label className="mb-1.5 block">Type</Label><Select value={form.providerType || ""} onValueChange={(value) => setForm({ ...form, providerType: value })}><SelectTrigger data-testid="guest-provider-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent>{PROVIDER_TYPES.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent></Select></div><div><Label className="mb-1.5 block">Nom de l'entreprise</Label><Input data-testid="guest-company-name" value={form.companyName || ""} onChange={(e) => setForm({ ...form, companyName: e.target.value })} /></div><div><Label className="mb-1.5 block">Fonction</Label><Input data-testid="guest-job-title" value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} /></div></div>}
              {form.category === "Exposant" && <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="guest-exposant-fields"><div><Label className="mb-1.5 block">Nom de la structure</Label><Input data-testid="guest-structure-name" value={form.structureName || ""} onChange={(e) => setForm({ ...form, structureName: e.target.value })} /></div><div><Label className="mb-1.5 block">Type de la structure</Label><Select value={form.structureType || ""} onValueChange={(value) => setForm({ ...form, structureType: value })}><SelectTrigger data-testid="guest-structure-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent>{STRUCTURE_TYPES.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent></Select></div><div><Label className="mb-1.5 block">Numéro du stand</Label><Input data-testid="guest-booth-number" value={form.boothNumber || ""} onChange={(e) => setForm({ ...form, boothNumber: e.target.value })} /></div><div><Label className="mb-1.5 block">Zone / Hall</Label><Input data-testid="guest-hall-zone" value={form.hallZone || ""} onChange={(e) => setForm({ ...form, hallZone: e.target.value })} /></div><div><Label className="mb-1.5 block">Fonction</Label><Input data-testid="guest-job-title" value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} /></div></div>}
              {form.category === "Presse" && <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="guest-presse-fields"><div><Label className="mb-1.5 block">Média</Label><Input data-testid="guest-media" value={form.media || ""} onChange={(e) => setForm({ ...form, media: e.target.value })} /></div><div><Label className="mb-1.5 block">Fonction</Label><Input data-testid="guest-job-title" value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} /></div></div>}
              {["Sponsor", "Partenaire"].includes(form.category) && <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="guest-organisation-fields"><div><Label className="mb-1.5 block">Nom de la structure</Label><Input data-testid="guest-structure-name" value={form.structureName || ""} onChange={(e) => setForm({ ...form, structureName: e.target.value })} /></div><div><Label className="mb-1.5 block">Type de la structure</Label><Select value={form.structureType || ""} onValueChange={(value) => setForm({ ...form, structureType: value })}><SelectTrigger data-testid="guest-structure-type"><SelectValue placeholder="Sélectionner" /></SelectTrigger><SelectContent>{STRUCTURE_TYPES.map((type) => <SelectItem key={type} value={type}>{type}</SelectItem>)}</SelectContent></Select></div><div><Label className="mb-1.5 block">Fonction</Label><Input data-testid="guest-job-title" value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} /></div></div>}
              {form.category === "Membre du personnel" && <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="guest-staff-fields"><div><Label className="mb-1.5 block">Fonction</Label><Input data-testid="guest-job-title" value={form.jobTitle || ""} onChange={(e) => setForm({ ...form, jobTitle: e.target.value })} /></div><label className="flex items-center gap-2 text-sm self-end" title="Permet d'accéder au tableau de bord Organisateur dans l'application mobile PlanItGreen"><Checkbox checked={!!form.mobileAdmin} onCheckedChange={(checked) => setForm({ ...form, mobileAdmin: !!checked })} data-testid="guest-mobile-admin" />Admin <span className="text-xs text-muted-foreground">ⓘ</span></label></div>}
              {showPrivileged && (
                <div className="mt-4 p-4 rounded-xl bg-violet-50 border border-violet-200">
                  <p className="text-sm font-semibold text-violet-700 mb-3">⚡ Accès privilégié</p>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="mb-1.5 block">Code d'accès unique</Label>
                      <div className="flex gap-2"><Input data-testid="guest-code" value={form.specialCode} onChange={(e) => setForm({ ...form, specialCode: e.target.value })} className="font-mono" />
                        <Button type="button" variant="outline" onClick={() => setForm({ ...form, specialCode: genCode(form.category, form.lastName) })} data-testid="generate-code"><Zap className="h-4 w-4" /></Button></div>
                      <p className="text-[11px] text-violet-700 mt-1.5">Ce code est celui envoyé par email à l'invité. Il doit le saisir à l'étape 1 de l'inscription pour débloquer son billet.</p></div>
                    <div><Label className="mb-1.5 block">Accompagnons autorisés</Label><Input type="number" data-testid="guest-companions" value={form.maxCompanions} onChange={(e) => setForm({ ...form, maxCompanions: e.target.value })} /><p className="text-[11px] text-violet-700 mt-1.5">Max billets avec ce code = accompagnants + 1.</p></div>
                  </div>
                  {form.specialCode && <p className="text-xs text-violet-700 mt-2">Code valable pour <b>{form.firstName || "l'invité"}</b> + {form.maxCompanions || 0} accompagnon(s)</p>}
                </div>
              )}
              <div className="flex justify-end gap-2 mt-4"><Button variant="outline" onClick={() => setShowForm(false)}>Annuler</Button><Button onClick={submit} data-testid="save-guest">{editId ? "Enregistrer" : "Ajouter"}</Button></div>
            </div>
          )}

          <div className="bg-card rounded-xl border border-border overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-10"><Checkbox onCheckedChange={toggleAll} checked={filtered.length > 0 && selected.length === filtered.filter((g) => !g.invitationSent).length} /></TableHead>
                <TableHead>Prénom</TableHead><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Catégorie</TableHead><TableHead>Code</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.length ? filtered.map((g) => (
                  <TableRow key={g.id} data-testid={`guest-${g.id}`}>
                    <TableCell><Checkbox disabled={g.invitationSent} checked={selected.includes(g.id)} onCheckedChange={(c) => setSelected((p) => c ? [...p, g.id] : p.filter((x) => x !== g.id))} data-testid={`select-guest-${g.id}`} /></TableCell>
                    <TableCell className="font-medium">{g.firstName}</TableCell><TableCell>{g.lastName}</TableCell>
                    <TableCell className="text-muted-foreground text-sm">{g.email}</TableCell>
                    <TableCell>{g.kind === "structure" ? <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-teal-100 text-teal-800"><Building2 className="h-3 w-3" />Structure · {g.structureName || "—"} · {g.codeUses || 0}/{g.maxUses || "∞"}</span> : <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{g.category}</span>}</TableCell>
                    <TableCell className="font-mono text-xs">{g.specialCode || "—"}</TableCell>
                    <TableCell>{g.status === "confirmed" ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-100 text-violet-700">Confirmé</span> : g.status === "declined" ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-rose-100 text-rose-700">Invitation déclinée</span> : g.invitationSent ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Invitation envoyée</span> : <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">En attente</span>}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      {g.status === "confirmed" ? (
                        <span className="inline-flex items-center gap-1 text-xs text-violet-700 font-semibold"><CheckCircle2 className="h-4 w-4" />Confirmé</span>
                      ) : g.invitationSent ? (
                        <>
                          <Button size="sm" variant="outline" className="mr-1" onClick={() => sendInvites([g.id], true)} data-testid={`resend-guest-${g.id}`}><Mail className="h-3.5 w-3.5 mr-1" />Renvoyer</Button>
                          <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><Lock className="h-3 w-3" />Verrouillé</span>
                        </>
                      ) : (
                        <>
                          <Button size="icon" variant="ghost" onClick={() => { setForm(g); setEditId(g.id); setShowForm(true); }} data-testid={`edit-guest-${g.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => removeGuest(g)} data-testid={`delete-guest-${g.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => sendInvites([g.id])} data-testid={`send-guest-${g.id}`}><Mail className="h-4 w-4 text-indigo-600" /></Button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )) : <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Aucun invité.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>

          {deleted.length > 0 && (
            <div className="bg-card rounded-xl border border-border p-5 mt-6">
              <h3 className="font-display font-semibold mb-3 text-rose-600">Historique des suppressions</h3>
              <Table>
                <TableHeader><TableRow><TableHead>Prénom</TableHead><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Catégorie</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
                <TableBody>{deleted.map((g) => <TableRow key={g.id}><TableCell>{g.firstName}</TableCell><TableCell>{g.lastName}</TableCell><TableCell>{g.email}</TableCell><TableCell>{g.category}</TableCell><TableCell>{formatDate(g.updatedAt)}</TableCell></TableRow>)}</TableBody>
              </Table>
            </div>
          )}
        </>
      ) : tab === "participants" ? (
        <Participants event={selectedEvent} />
      ) : (
        <Results />
      )}
    </>
  );
}

function Participants({ event }) {
  const { guestsByEvent } = useData();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [sortBy, setSortBy] = useState("amount_desc");
  const [refundGroup, setRefundGroup] = useState(null);
  const [refundAmt, setRefundAmt] = useState("");
  const [refunding, setRefunding] = useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    client.get(`/participants/${event.id}`).then(({ data }) => setRows(data || [])).catch(() => setRows([])).finally(() => setLoading(false));
  }, [event.id]);
  React.useEffect(() => { load(); }, [load]);

  // Map participantId → guest.category (source of truth for the effective category)
  const guestCatByPid = React.useMemo(() => {
    const m = {};
    (guestsByEvent(event.id) || []).forEach((g) => { if (g.participantId && g.category) m[g.participantId] = g.category; });
    return m;
  }, [guestsByEvent, event.id]);

  const effCat = React.useCallback((p) => (p.isStructure ? "Structure" : (guestCatByPid[p.id] || "Standard")), [guestCatByPid]);

  const filteredRows = rows
    .filter((r) => cat === "all" || effCat(r) === cat)
    .filter((r) => !q || `${r.firstName} ${r.lastName} ${r.email}`.toLowerCase().includes(q.toLowerCase()));

  // Group participants by (email + lastName) — same person = single row, but 2 personnes différentes
  // avec le même email restent séparées (cas structure : plusieurs invités partagent l'email de la structure).
  const grouped = React.useMemo(() => {
    const map = new Map();
    filteredRows.forEach((r) => {
      const key = ((r.email || "").toLowerCase().trim() + "|" + (r.lastName || "").toLowerCase().trim() + "|" + (r.firstName || "").toLowerCase().trim()) || r.id;
      const g = map.get(key) || {
        key, email: r.email, firstName: r.firstName, lastName: r.lastName,
        participants: [], quantity: 0, amountPaid: 0, refundedAmount: 0,
        effectiveCategory: effCat(r), tickets: {}, status: "paid",
      };
      g.participants.push(r);
      g.quantity += Number(r.quantity || 0);
      g.amountPaid += Number(r.amount || 0);
      g.refundedAmount += Number(r.refundedAmount || 0);
      const tName = r.isStructure ? "Billet Structure" : ((r.customFields?.attendees?.[0]?.ticket) || r.category || "Billet");
      g.tickets[tName] = (g.tickets[tName] || 0) + Number(r.quantity || 0);
      // Prefer any privileged category over "Standard" for the group
      if (g.effectiveCategory === "Standard" && effCat(r) !== "Standard") g.effectiveCategory = effCat(r);
      map.set(key, g);
    });
    // derive aggregate status per group + sort
    const arr = [...map.values()].map((g) => {
      const anyPaid = g.participants.some((p) => p.status === "paid");
      const anyPartial = g.participants.some((p) => p.status === "partially_refunded" || Number(p.refundedAmount || 0) > 0);
      const allRefunded = g.participants.every((p) => p.status === "refunded");
      const status = allRefunded ? "refunded" : anyPartial ? "partially_refunded" : anyPaid ? "paid" : "registered";
      const remaining = Math.max(0, +(g.amountPaid - g.refundedAmount).toFixed(2));
      return { ...g, status, remaining };
    });
    const cmp = {
      amount_desc: (a, b) => b.amountPaid - a.amountPaid,
      amount_asc: (a, b) => a.amountPaid - b.amountPaid,
      name_asc: (a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, "fr"),
      name_desc: (a, b) => `${b.lastName} ${b.firstName}`.localeCompare(`${a.lastName} ${a.firstName}`, "fr"),
      qty_desc: (a, b) => b.quantity - a.quantity,
    }[sortBy] || ((a, b) => b.amountPaid - a.amountPaid);
    return arr.sort(cmp);
  }, [filteredRows, effCat, sortBy]);

  const remove = async (g) => {
    if (!window.confirm(`Supprimer ${g.firstName} ${g.lastName} (${g.participants.length} inscription${g.participants.length > 1 ? "s" : ""}) ?`)) return;
    try {
      await Promise.all(g.participants.map((p) => client.delete(`/participants/${p.id}`)));
      setRows((p) => p.filter((r) => !g.participants.find((pp) => pp.id === r.id)));
      toast.success("Participant supprimé");
    } catch { toast.error("Suppression impossible"); }
  };

  const openRefund = (g) => {
    setRefundGroup(g);
    setRefundAmt(g.remaining.toFixed(2));
  };

  const confirmRefund = async () => {
    if (!refundGroup) return;
    const amt = Number(String(refundAmt).replace(",", "."));
    if (!(amt > 0)) { toast.error("Montant invalide"); return; }
    if (amt > refundGroup.remaining + 0.001) {
      toast.error(`Le montant dépasse le restant remboursable (${euro(refundGroup.remaining)})`);
      return;
    }
    setRefunding(true);
    try {
      // Allocate greedily across paid participants (largest remaining first)
      let left = amt;
      const paidParts = [...refundGroup.participants]
        .filter((p) => (Number(p.amount || 0) - Number(p.refundedAmount || 0)) > 0.001 && p.status !== "refunded")
        .sort((a, b) => (Number(b.amount || 0) - Number(b.refundedAmount || 0)) - (Number(a.amount || 0) - Number(a.refundedAmount || 0)));
      for (const p of paidParts) {
        if (left <= 0.001) break;
        const remP = Number(p.amount || 0) - Number(p.refundedAmount || 0);
        const chunk = Math.min(left, remP);
        // eslint-disable-next-line no-await-in-loop
        await client.post(`/participants/${p.id}/refund`, { amount: +chunk.toFixed(2) });
        left = +(left - chunk).toFixed(2);
      }
      toast.success(`Remboursement de ${euro(amt)} effectué`);
      setRefundGroup(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Remboursement impossible");
    } finally { setRefunding(false); }
  };

  const exportExcel = () => {
    const data = grouped.map((g) => ({
      Prénom: g.firstName, Nom: g.lastName, Email: g.email,
      Catégorie: g.effectiveCategory,
      Billets: Object.entries(g.tickets).map(([n, q]) => `${q}× ${n}`).join(", "),
      "Total payé (€)": g.amountPaid, "Remboursé (€)": g.refundedAmount,
      Statut: g.status === "refunded" ? "Remboursé" : g.status === "partially_refunded" ? "Partiel" : g.status === "paid" ? "Payé" : "Inscrit (gratuit)",
    }));
    const ws = XLSX.utils.json_to_sheet(data.length ? data : [{ Info: "Aucun participant" }]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Participants");
    XLSX.writeFile(wb, `participants-${(event.name || "evenement").toLowerCase().replace(/[^a-z0-9]+/g, "-")}.xlsx`);
    toast.success("Export Excel généré");
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Input placeholder="Rechercher par nom ou email..." className="w-64" value={q} onChange={(e) => setQ(e.target.value)} data-testid="participant-search" />
        <Select value={cat} onValueChange={setCat}><SelectTrigger className="w-52" data-testid="participant-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            <SelectItem value="Standard">Standard</SelectItem>
            <SelectItem value="Structure">Structure</SelectItem>
            {PRIVILEGED.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent></Select>
        <Select value={sortBy} onValueChange={setSortBy}><SelectTrigger className="w-56" data-testid="participant-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="amount_desc">Total payé (décroissant)</SelectItem>
            <SelectItem value="amount_asc">Total payé (croissant)</SelectItem>
            <SelectItem value="name_asc">Nom (A → Z)</SelectItem>
            <SelectItem value="name_desc">Nom (Z → A)</SelectItem>
            <SelectItem value="qty_desc">Nombre de billets</SelectItem>
          </SelectContent></Select>
        <span className="text-sm text-muted-foreground">{grouped.length} participant(s)</span>
        <Button size="sm" variant="outline" className="ml-auto" onClick={exportExcel} data-testid="participants-export"><Download className="h-4 w-4 mr-1" />Exporter Excel</Button>
      </div>
      <div className="bg-card rounded-xl border border-border overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Nom</TableHead><TableHead>Prénom</TableHead><TableHead>Email</TableHead><TableHead>Catégorie</TableHead><TableHead>Billets</TableHead><TableHead className="text-right">Total payé</TableHead><TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {loading ? <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Chargement…</TableCell></TableRow>
              : grouped.length ? grouped.map((g) => (
              <TableRow key={g.key} data-testid={`participant-${g.key}`}>
                <TableCell className="font-medium">{g.lastName}</TableCell>
                <TableCell>{g.firstName}</TableCell>
                <TableCell className="text-muted-foreground text-sm">{g.email}</TableCell>
                <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${g.effectiveCategory === "Standard" ? "bg-slate-100 text-slate-700" : "bg-violet-100 text-violet-700"}`} data-testid={`participant-cat-${g.key}`}>{g.effectiveCategory}</span></TableCell>
                <TableCell><div className="flex flex-wrap gap-1">{Object.entries(g.tickets).map(([n, q]) => (<span key={n} className="text-xs px-2 py-0.5 rounded-full bg-slate-100">{q}× {n}</span>))}</div></TableCell>
                <TableCell className="text-right">
                  {g.status === "refunded" ? <span className="text-xs text-rose-600">Remboursé</span>
                    : g.amountPaid ? (<span>{euro(g.amountPaid)}{g.refundedAmount > 0 && <span className="text-xs text-rose-500 ml-1">(− {euro(g.refundedAmount)})</span>}</span>)
                    : <span className="text-xs text-slate-400">Gratuit</span>}
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button size="sm" variant="outline" className="mr-1" disabled={g.remaining <= 0} onClick={() => openRefund(g)} data-testid={`refund-${g.key}`}>Rembourser</Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(g)} data-testid={`delete-participant-${g.key}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </TableCell>
              </TableRow>
            )) : <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Aucun participant inscrit pour l'instant. Les inscriptions depuis la page publique apparaîtront ici.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!refundGroup} onOpenChange={(o) => !o && setRefundGroup(null)}>
        <DialogContent className="max-w-md" data-testid="refund-dialog">
          <DialogHeader>
            <DialogTitle>Rembourser {refundGroup?.firstName} {refundGroup?.lastName}</DialogTitle>
            <DialogDescription>
              Total payé : <b>{refundGroup ? euro(refundGroup.amountPaid) : ""}</b>
              {refundGroup?.refundedAmount > 0 && <> · Déjà remboursé : <b>{euro(refundGroup.refundedAmount)}</b></>}
              <br />Reste remboursable : <b className="text-primary">{refundGroup ? euro(refundGroup.remaining) : ""}</b>
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label>Montant à rembourser (€)</Label>
            <Input type="number" step="0.01" min="0.01" max={refundGroup?.remaining || 0} value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} data-testid="refund-amount-input" autoFocus />
            <p className="text-xs text-muted-foreground">Le remboursement sera effectué sur la carte Stripe utilisée lors du paiement.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundGroup(null)} disabled={refunding} data-testid="refund-cancel">Annuler</Button>
            <Button onClick={confirmRefund} disabled={refunding} data-testid="refund-confirm">{refunding ? "Traitement…" : "Confirmer le remboursement"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Results() {
  const s = STATIC;
  const revenuePie = [
    { name: "Entreprise", value: 31200 },
    { name: "Association", value: 6800 },
    { name: "Centre de formation/Université", value: 5200 },
    { name: "Collectivité/Établissement public", value: 3500 },
    { name: "Autre", value: 2050 },
  ];
  return (
    <>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5"><p className="text-xs font-medium text-muted-foreground uppercase">Taux de remplissage</p><p className="font-display text-2xl font-bold mt-1">{s.fillRate}%</p><ProgressBar pct={s.fillRate} className="mt-2" /><p className="text-xs text-muted-foreground mt-1">{s.confirmedCount}/{s.maxParticipants} confirmés</p></div>
        <KpiCard tint="violet" label="Revenus" value={euro(s.revenue)} />
        <KpiCard tint="emerald" label="Taux de conversion" value={`${s.convRate}%`} />
        <KpiCard tint="amber" label="En attente" value={s.pendingCount} />
      </div>

      <div className="grid sm:grid-cols-4 gap-4 mb-6">
        {[["Confirmés", s.confirmedCount, "bg-emerald-500"], ["En attente", s.pendingCount, "bg-amber-500"], ["Déclinés", s.declinedCount, "bg-rose-500"], ["Remboursés", s.refundedCount, "bg-slate-400"]].map(([l, v, c]) => (
          <div key={l} className="bg-card rounded-xl border border-border p-4"><div className="flex justify-between text-sm mb-2"><span>{l}</span><b>{v}</b></div><div className="h-2 rounded-full bg-slate-100 overflow-hidden"><div className={`h-full ${c}`} style={{ width: `${(v / s.totalInvites) * 100}%` }} /></div></div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Inscriptions par catégorie</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={s.byCategory} layout="vertical"><CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#eef2f7" /><XAxis type="number" tick={{ fontSize: 11 }} /><YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 10 }} /><Tooltip /><Legend />
              <Bar dataKey="count" name="Inscrits" radius={[0, 4, 4, 0]}>{s.byCategory.map((entry, index) => <Cell key={entry.name} fill={CATEGORY_CHART_COLORS[index % CATEGORY_CHART_COLORS.length]} />)}</Bar><Bar dataKey="confirmed" name="Confirmés" fill="#10b981" radius={[0, 4, 4, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Revenus par type de structure</h3>
          <div className="flex items-center gap-4">
            <ResponsiveContainer width="55%" height={220}>
              <PieChart><Pie data={revenuePie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80}>{revenuePie.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}</Pie><Tooltip formatter={(v) => euro(v)} /></PieChart>
            </ResponsiveContainer>
            <div className="flex-1 space-y-2">{revenuePie.map((r, i) => (<div key={r.name} className="flex items-center gap-2 text-sm"><span className="h-3 w-3 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} /><span className="flex-1">{r.name}</span><b>{euro(r.value)}</b></div>))}</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Courbe des ventes</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={s.salesByDay}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eef2f7" /><XAxis dataKey="day" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="cumul" name="Ventes cumulées" fill="#8b5cf6" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-card rounded-xl border border-border p-5">
          <h3 className="font-display font-semibold mb-4">Utilisation des codes d'accès</h3>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(s.codesUsed).map(([code, n]) => (<div key={code} className="p-3 rounded-lg bg-muted"><p className="font-mono text-xs">{code}</p><p className="text-sm font-semibold mt-1">{n} utilisation(s)</p></div>))}
          </div>
          <div className="mt-4 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
            <p className="text-sm font-semibold text-emerald-700">PlanItGreen — Check-in sur place</p>
            <p className="text-2xl font-display font-bold text-emerald-800 mt-1">247 <span className="text-base font-normal">/ 312 attendus · 79%</span></p>
          </div>
        </div>
      </div>
    </>
  );
}
